import { setCurrentWatcher, clearCurrentWatcher, unscheduleHandler, getCurrentJobId, scheduleWork } from './engine.js';
import { reactive } from './reactive.js';

const MAX_SET_COUNT = 1000;
const handlers = new WeakSet();

const createHandler = ({ target, key, proxy, context, type, fn, deep }) => {
    const isComputedProperty = type === 'computedProperty';
    const isObserveFunction = type === 'observeFunction';
    const isReactiveProperty = type === 'reactiveProperty';
    const isDependency = isReactiveProperty || isComputedProperty;

    const handler = {
        type,
        dispose() {
            unscheduleHandler(handler.notify);
            if (handler.dependencies) {
                handler.dependencies.forEach((dependency) => {
                    dependency.removeWatcher(handler);
                });
            }
        },
        isComputedProperty,
        isObserveFunction,
        isReactiveProperty,
        isDependency,
        dependencies: undefined,
        freshDependencies: undefined,
        watchers: undefined,
    };

    if (isComputedProperty) {
        handler.dirty = true;
    }

    if (isComputedProperty || isObserveFunction) {
        handler.invoke = () => {
            setCurrentWatcher(handler);
            if (isComputedProperty) {
                handler.dirty = false;
                const value = fn(proxy, { context });
                if (value !== target[key]) {
                    target[key] = deep ? reactive(value, { deep, context }) : value;
                }
            } else {
                fn();
            }
            clearCurrentWatcher();

            let oldDependencies;
            if (handler.dependencies) {
                // remove dependencies that are not used in the old set anymore
                handler.dependencies.forEach((dependency) => {
                    if (handler.freshDependencies && !handler.freshDependencies.has(dependency)) {
                        dependency.removeWatcher(handler);
                    }
                });
                handler.dependencies.clear();
                oldDependencies = handler.dependencies; // keep the reference from the empty set
            }

            handler.dependencies = handler.freshDependencies;
            handler.freshDependencies = oldDependencies; // reuse the empty set for the next round
        };

        handler.notify = () => {
            if (isComputedProperty) {
                if (handler.watchedCount) {
                    handler.invoke();
                } else {
                    handler.dirty = true;
                }
            } else {
                handler.invoke();
            }
        };

        handler.addDependency = (dependency) => {
            if (!handler.freshDependencies) {
                handler.freshDependencies = new Set();
            }
            handler.freshDependencies.add(dependency);
        };
    }

    if (isReactiveProperty || isComputedProperty) {
        handler.target = target;
        handler.key = key;
        handler.watchedCount = 0;

        handler.addWatcher = (watcher) => {
            if (!handler.watchers) {
                handler.watchers = new Set();
            }
            if (!handler.watchers.has(watcher)) {
                handler.watchers.add(watcher);
                if (watcher.isObserveFunction || watcher.watchedCount) {
                    handler.incrementWatchedCount();
                }
            }
        };

        handler.removeWatcher = (watcher) => {
            if (handler.watchers.has(watcher)) {
                handler.watchers.delete(watcher);
                if (watcher.isObserveFunction || watcher.watchedCount) {
                    handler.decrementWatchedCount();
                }
            }
        };

        handler.incrementWatchedCount = () => {
            handler.watchedCount += 1;
            if (handler.watchedCount === 1) {
                if (handler.dependencies) {
                    handler.dependencies.forEach((dependency) => {
                        dependency.incrementWatchedCount();
                    });
                }
            }
        };

        handler.decrementWatchedCount = () => {
            handler.watchedCount -= 1;
            if (handler.watchedCount === 0) {
                if (handler.dependencies) {
                    handler.dependencies.forEach((dependency) => {
                        dependency.decrementWatchedCount();
                    });
                }
            }
        };

        handler.getWatchers = () => {
            return handler.watchers;
        };
    }

    if (isReactiveProperty) {
        let lastJobId;
        let setCount;
        handler.set = (value) => {
            scheduleWork();
            const currentJobId = getCurrentJobId();
            if (currentJobId === lastJobId) {
                setCount += 1;
                if (setCount === MAX_SET_COUNT) {
                    throw new Error(`Potential infinite loop occured for key '${key}'`);
                }
            } else {
                lastJobId = currentJobId;
                setCount = 1;
            }

            target[key] = deep ? reactive(value, { deep, context }) : value;
        };
    }

    handlers.add(handler);

    return handler;
};

export { createHandler };
